import { untrack } from "solid-js";

type NavigateFn = (opts: { to: string; replace?: boolean }) => Promise<unknown> | void;

/**
 * Guest-guard effect of `/profile/delete-account`: a guest has no
 * account to delete, so as soon as session boot/priming finishes we
 * return them to the profile. Extracted to a pure function (outside the route component)
 * to be able to test in isolation, with `vitest`, the re-entrancy mechanism
 * that broke the page — without pulling in the entire route tree (providers,
 * PowerSync, etc.) that `routes/_app/route.tsx` imports.
 *
 * BUG (canon: custodia-parental-app@7593164, "breaks the reactive loop of the
 * guest-redirect in delete-account"). The original version read
 * `session.isPending` AND `session.isVisitor()` reactively and called
 * `navigate()` without any idempotency guard:
 *
 *   createEffect(() => {
 *     if (!session.isPending && session.isVisitor()) {
 *       void navigate({ to: "/profile", replace: true });
 *     }
 *   });
 *
 * In THIS repo `useSession().isVisitor()` (`src/lib/auth.ts`) resolves
 * per-tab identity from `activePathname()` (the URL `/u/<n>/...`),
 * so reading it inside an effect makes it depend —in a hidden way— on the
 * URL. `navigate()` changes that URL SYNCHRONOUSLY (real pushState), which
 * revalidates `isVisitor()` while the effect itself remains in its call stack: without
 * a guard, SolidJS re-triggers the same effect re-entrantly inside its
 * synchronous scheduler, never releasing the JS thread — the page stays
 * blank for the guest (`#app-loader` never disappears).
 *
 * Fix (same idiom as the centralized redirect of `BootManager`):
 *   1. Guard by the ACTUAL route (`pathname`) — remains true whatever
 *      happens with the router mount cycle, so as soon as real navigation
 *      takes effect this function becomes a deterministic no-op
 *      (stops depending on whether `isVisitor()` keeps being read).
 *   2. `untrack()` on `isVisitor()` — it is not the condition that must TRIGGER the
 *      effect, it is only queried once `isPending` resolves.
 *
 * Covered by `__tests__/delete-account-guest-guard.test.ts`.
 */
export function runDeleteAccountGuestGuard(deps: {
  pathname: () => string;
  isPending: () => boolean;
  isVisitor: () => boolean;
  navigate: NavigateFn;
}): void {
  const onDeleteAccountPage = deps.pathname().endsWith("/profile/delete-account");
  if (!onDeleteAccountPage) {
    return;
  }
  if (!deps.isPending() && untrack(() => deps.isVisitor())) {
    void deps.navigate({ to: "/profile", replace: true });
  }
}
